import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL } from "../config/api";

const BACKEND_API_URL = API_URL;

// Frontend Department interface (includes optional fields not in backend)
export interface Department {
  id: string;
  name: string;
  industry?: string;
  location?: string;
  team?: string;
}

// Backend request format for createDepartment
interface CreateDepartmentRequest {
  companyId: string;
  name: string;
  location?: string;
  team?: string;
  industry?: string;
}

interface UpdateDepartmentRequest extends CreateDepartmentRequest {
  departmentId: string;
}

type DepartmentPayload = Omit<Department, "id">;

interface DepartmentsState {
  departments: Department[];
  loading: boolean;
  error: string | null;
}

const initialState: DepartmentsState = {
  departments: [],
  loading: false,
  error: null,
};

export const createDepartment = createAsyncThunk(
  "departments/createDepartment",
  async (departmentData: DepartmentPayload, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } };
      const token = state.auth.token;
      const companyId = state.auth.user?.companyId || state.auth.user?.id;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!companyId) {
        return rejectWithValue("No company ID found");
      }

      const requestData: CreateDepartmentRequest = {
        companyId,
        name: departmentData.name,
        location: departmentData.location || "",
        team: departmentData.team || "",
        industry: departmentData.industry || "",
      };

      const response = await axios.post(
        `${BACKEND_API_URL}/company/createDepartment`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Backend returns 204 No Content, so use request data + generate ID from response headers or use request data
      // Try to get ID from response header first
      const departmentId = response.headers?.['location']?.split('/')?.pop() ||
                          response.data?.id ||
                          response.data?.departmentId ||
                          `temp-${Date.now()}`;

      // Return created department using request data since backend returns 204
      return {
        id: departmentId,
        name: requestData.name,
        location: requestData.location,
        team: requestData.team,
        industry: requestData.industry,
      } as Department;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to create department"
      );
    }
  }
);

export const updateDepartment = createAsyncThunk(
  "departments/updateDepartment",
  async (
    {
      departmentId,
      departmentData,
    }: { departmentId: string; departmentData: DepartmentPayload },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } };
      const token = state.auth.token;
      const companyId = state.auth.user?.companyId || state.auth.user?.id;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!companyId) {
        return rejectWithValue("No company ID found");
      }

      const requestData: UpdateDepartmentRequest = {
        companyId,
        departmentId,
        name: departmentData.name,
        location: departmentData.location || "",
        team: departmentData.team || "",
        industry: departmentData.industry || "",
      };

      await axios.put(
        `${BACKEND_API_URL}/company/updateDepartment`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return {
        id: departmentId,
        name: requestData.name,
        location: requestData.location,
        team: requestData.team,
        industry: requestData.industry,
      } as Department;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update department"
      );
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  "departments/fetchDepartments",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } };
      const token = state.auth.token;
      const companyId = state.auth.user?.companyId || state.auth.user?.id;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!companyId) {
        return rejectWithValue("No company ID found");
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/company/listdepartments/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Map backend response to frontend Department interface
      return response.data.map((dept: any) => ({
        id: dept.id || dept.departmentId,
        name: dept.name,
        location: dept.location,
        team: dept.team,
        industry: dept.industry,
      })) as Department[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch departments"
      );
    }
  }
);

export const deleteDepartment = createAsyncThunk(
  "departments/deleteDepartment",
  async (departmentId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; user: { companyId?: string; id?: string } | null } };
      const token = state.auth.token;
      const companyId = state.auth.user?.companyId || state.auth.user?.id;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      if (!companyId) {
        return rejectWithValue("No company ID found");
      }

      await axios.delete(
        `${BACKEND_API_URL}/company/deleteDepartment/${companyId}/${departmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return departmentId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete department"
      );
    }
  }
);

const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createDepartment.fulfilled,
        (state, action: PayloadAction<Department>) => {
          state.loading = false;
          state.departments.push(action.payload);
        }
      )
      .addCase(createDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to create department";
      })
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDepartments.fulfilled,
        (state, action: PayloadAction<Department[]>) => {
          state.loading = false;
          state.departments = action.payload;
        }
      )
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch departments";
      })
      .addCase(deleteDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteDepartment.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.departments = state.departments.filter(
            (department) => department.id !== action.payload
          );
        }
      )
      .addCase(
        updateDepartment.fulfilled,
        (state, action: PayloadAction<Department>) => {
          state.loading = false;
          state.departments = state.departments.map((department) =>
            department.id === action.payload.id
              ? { ...department, ...action.payload }
              : department
          );
        }
      )
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to delete department";
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to update department";
      });
  },
});

export const { clearError } = departmentsSlice.actions;
export default departmentsSlice.reducer;
